import type { HookAPI } from 'antd/es/modal/useModal'
import { useRef, useState } from 'react'

import type { OrganisationShortType } from '@/types'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'

import {
  isScrolledToBottom,
  type OrganisationOption,
  ORGANISATIONS_PAGE_SIZE,
  SEARCH_DEBOUNCE,
} from './SubscriptionCreationModal.config'

function useOrganisationSearch(modal: HookAPI) {
  const [options, setOptions] = useState<OrganisationOption[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const queryRef = useRef('')
  const isOpenRef = useRef(false)
  const lastFetchedRef = useRef<string | null>(null)
  const isClosingRef = useRef(false)

  const fetchOrganisations = (
    query: string,
    nextPage: number,
    append: boolean
  ) => {
    setLoading(true)
    api
      .get('/organisations', {
        params: {
          description: query,
          pageSize: ORGANISATIONS_PAGE_SIZE,
          page: nextPage,
        },
      })
      .then((response) => {
        const content: OrganisationShortType[] =
          response.data.organisations?.content ?? response.data.content ?? []
        const total: number = response.data.organisations?.totalElements ?? 0
        const nextOptions = content.map((org) => ({
          value: org.id,
          label: org.description,
          org,
        }))
        setOptions((prev) => (append ? [...prev, ...nextOptions] : nextOptions))
        setHasMore((nextPage + 1) * ORGANISATIONS_PAGE_SIZE < total)
        setPage(nextPage)
      })
      .catch((error) => handleApiError(error, modal))
      .finally(() => setLoading(false))
  }

  const handleOpenChange = (open: boolean) => {
    isOpenRef.current = open
    isClosingRef.current = !open
    if (!open) return
    lastFetchedRef.current = queryRef.current
    fetchOrganisations(queryRef.current, 0, false)
  }

  const handleSearch = (query: string) => {
    if (!isOpenRef.current) return
    if (isClosingRef.current && query === '') return
    setSearchValue(query)
    queryRef.current = query
    if (query === lastFetchedRef.current) return
    setTimeout(() => {
      if (queryRef.current !== query) return
      lastFetchedRef.current = query
      fetchOrganisations(query, 0, false)
    }, SEARCH_DEBOUNCE)
  }

  const handlePopupScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (isScrolledToBottom(event.currentTarget) && hasMore && !loading) {
      fetchOrganisations(queryRef.current, page + 1, true)
    }
  }

  const resetQuery = () => {
    setSearchValue('')
    queryRef.current = ''
    lastFetchedRef.current = null
  }

  const clearOptions = () => {
    resetQuery()
    setOptions([])
  }

  return {
    options,
    loading,
    searchValue,
    setSearchValue,
    handleOpenChange,
    handleSearch,
    handlePopupScroll,
    resetQuery,
    clearOptions,
  }
}

export default useOrganisationSearch
